import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Snackbar } from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useLocation, useNavigate } from 'react-router-dom';
import { deleteClients, getClients } from 'utils/Clients';
import { ClientsDataTable } from './ClientsDataTable';
import { ClientsContextMenu } from './ClientsContextMenu';
import { ClientDialog } from 'components/dialog/clients/ClientDialog';
import { InfoClient } from 'components/dialog/clients/InfoClient';
import { CreateClientFormDialog } from 'components/dialog/clients/CreateClientFormDialog';
import { ClientCreateResponseDialog } from 'components/dialog/clients/ClientCreateResponseDialog';
import {
    buildUserManagementClientsActionPath,
    buildUserManagementClientsBasePath,
    parseUserManagementClientsRoute,
} from 'utils/userManagementClientsRouting';

interface ContextMenuState {
    mouseX: number;
    mouseY: number;
}

const ClientsTableContainer: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState<ClientTableRow[]>([]);
    const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
    const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const [openCreateResponseDialog, setOpenCreateResponseDialog] = useState(false);
    const [createdClient, setCreatedClient] = useState<ClientCreateResult | null>(null);
    const [openAlert, setOpenAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertSeverity, setAlertSeverity] = useState<'error' | 'success'>('success');

    const fetchClients = useCallback(async () => {
        await getClients(setLoading, setRows);
    }, []);

    const routeAction = useMemo(
        () => parseUserManagementClientsRoute(location.pathname),
        [location.pathname]
    );

    const handleOpenContextMenu = useCallback((event: React.MouseEvent, rowId?: string) => {
        event.preventDefault();

        if (rowId && !selectedRows.includes(rowId)) {
            setSelectedRows([rowId]);
        }

        setContextMenu({ mouseX: event.clientX - 2, mouseY: event.clientY - 4 });
    }, [selectedRows]);

    const selectedClient = useMemo(() => {
        if (selectedRows.length !== 1) {
            return null;
        }
        return rows.find((row) => row.id === selectedRows[0]) ?? null;
    }, [rows, selectedRows]);

    useEffect(() => {
        if (routeAction.action === 'add') {
            setOpenCreateDialog(true);
            setOpenViewDialog(false);
            return;
        }

        if (routeAction.action === 'detail' && routeAction.id) {
            setSelectedRows([routeAction.id]);
            setOpenViewDialog(true);
            setOpenCreateDialog(false);
            return;
        }

        if (routeAction.action === 'edit' && routeAction.id) {
            setSelectedRows([routeAction.id]);
            setOpenCreateDialog(false);
            setOpenViewDialog(false);
            return;
        }

        setOpenCreateDialog(false);
        setOpenViewDialog(false);
    }, [routeAction]);

    const canView = selectedRows.length === 1;
    const canEdit = false;
    const canDelete = selectedRows.length > 1;

    const handleDelete = async () => {
        if (!canDelete) {
            return;
        }

        const ids = selectedRows.map((id) => String(id));
        const success = await deleteClients(setLoading, ids);

        setAlertMessage(success ? 'Clientes eliminados correctamente' : 'No fue posible eliminar los clientes');
        setAlertSeverity(success ? 'success' : 'error');
        setOpenAlert(true);

        if (success) {
            setSelectedRows([]);
            setContextMenu(null);
            await fetchClients();
        }
    };

    return (
        <>
            <ClientsDataTable
                rows={rows}
                loading={loading}
                selectedRows={selectedRows}
                onSelectionChange={setSelectedRows}
                onFetchData={fetchClients}
                onOpenContextMenu={handleOpenContextMenu}
            />

            <ClientsContextMenu
                contextMenu={contextMenu}
                canView={canView}
                canEdit={canEdit}
                canDelete={canDelete}
                onClose={() => setContextMenu(null)}
                onCreate={() => {
                    navigate(buildUserManagementClientsActionPath('add'));
                    setContextMenu(null);
                }}
                onView={() => {
                    if (!selectedClient) {
                        setContextMenu(null);
                        return;
                    }
                    navigate(
                        buildUserManagementClientsActionPath(
                            'detail',
                            selectedClient.id
                        )
                    );
                    setContextMenu(null);
                }}
                onEdit={() => {
                    if (!selectedClient) {
                        setContextMenu(null);
                        return;
                    }

                    navigate(
                        buildUserManagementClientsActionPath(
                            'edit',
                            selectedClient.id
                        )
                    );
                    setContextMenu(null);
                }}
                onDelete={handleDelete}
            />

            <CreateClientFormDialog
                open={openCreateDialog}
                onClose={() => navigate(buildUserManagementClientsBasePath())}
                onCreated={(result) => {
                    setCreatedClient(result);
                    setOpenCreateResponseDialog(true);
                    fetchClients();
                    navigate(buildUserManagementClientsBasePath());
                }}
            />

            <ClientCreateResponseDialog
                open={openCreateResponseDialog}
                data={createdClient}
                onClose={() => {
                    setOpenCreateResponseDialog(false);
                    setCreatedClient(null);
                }}
            />

            <ClientDialog
                open={openViewDialog && !!selectedClient}
                title='Detalle del cliente'
                onClose={() => navigate(buildUserManagementClientsBasePath())}
            >
                {selectedClient && <InfoClient client={selectedClient} />}
            </ClientDialog>

            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={openAlert}
                autoHideDuration={3500}
                onClose={() => setOpenAlert(false)}
            >
                <Alert severity={alertSeverity} onClose={() => setOpenAlert(false)} sx={{ width: '100%' }}>
                    {alertMessage}
                </Alert>
            </Snackbar>
        </>
    );
};

export { ClientsTableContainer };
